/**
 * GTIN validation, normalisation and check-digit repair for the web client.
 * Pure mathematical functions — safe for browser execution.
 */

/** mod-10 check digit for a GTIN body. */
export function gtinCheckDigit(body: string): number {
  let sum = 0;
  for (let i = 0; i < body.length; i++) {
    const digit = Number(body[body.length - 1 - i]);
    sum += digit * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10;
}

/** True when `code` is digits-only, length 8/12/13/14, and its check digit validates. */
export function isValidGtin(code: string): boolean {
  if (!/^\d+$/.test(code)) return false;
  if (![8, 12, 13, 14].includes(code.length)) return false;
  return gtinCheckDigit(code.slice(0, -1)) === Number(code.at(-1));
}

/** Normalise any GTIN to EAN-13 where that is legitimate (UPC-A prefixed with 0, GTIN-14 stripped). */
export function toEan13(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 12) return `0${digits}`;
  if (digits.length === 13) return digits;
  if (digits.length === 14) return digits.slice(1);
  return null;
}

/**
 * Repair a GTIN whose length is 13, 12, 8, or 14 but whose check digit is off
 * (common clerical typo on the last digit in scraped e-commerce catalogs).
 * Returns a valid EAN-13 (or EAN-8) code with mathematically calculated mod-10 check digit, or null if invalid format.
 */
export function repairGtinCheckDigit(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 12) {
    const body = `0${digits.slice(0, 11)}`;
    return `${body}${gtinCheckDigit(body)}`;
  }
  if (digits.length === 13) {
    const body = digits.slice(0, 12);
    return `${body}${gtinCheckDigit(body)}`;
  }
  if (digits.length === 8) {
    const body = digits.slice(0, 7);
    return `${body}${gtinCheckDigit(body)}`;
  }
  if (digits.length === 14) {
    const body = digits.slice(1, 13);
    return `${body}${gtinCheckDigit(body)}`;
  }
  return null;
}
